'use client'

/* eslint-disable @next/next/no-img-element */

import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartData,
  type Chart,
  type ChartOptions,
  type ScriptableContext,
  type ScriptableLineSegmentContext,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { CloseIcon } from '@/components/ui/icons'
import { getPoolAccentColor } from '@/lib/pool-theme'
import type { PoolMatch } from '@/lib/services/competitions-hub'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler)

function logoProxyUrl(src: string) {
  return `/api/logo?src=${encodeURIComponent(src)}`
}

function shortTeamName(name: string | null | undefined) {
  return (name ?? '')
    .replace(/(A\.S\.D\.|ASD|SSD|Srl|SRL|U\.S\.)/g, '')
    .replace(/VOLLEY/g, 'Volley')
    .trim()
}

function teamInitials(name: string | null | undefined) {
  const cleaned = shortTeamName(name)
  if (!cleaned) return '??'
  return cleaned
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

function normalizeTeamKey(name: string) {
  return name
    .toLowerCase()
    .replace(/a\.s\.d\.|asd|ssd|srl|u\.s\./g, '')
    .replace(/volley/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function parseSetPoints(sets: string | null) {
  if (!sets) return null
  const pairs = sets.match(/(\d{1,2})-(\d{1,2})/g)
  if (!pairs?.length) return null
  let home = 0
  let away = 0
  for (const pair of pairs) {
    const match = pair.match(/(\d{1,2})-(\d{1,2})/)
    if (!match) continue
    home += Number.parseInt(match[1], 10)
    away += Number.parseInt(match[2], 10)
  }
  return { home, away }
}

function formatDecimal(value: number, digits = 3) {
  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value)
}

interface TeamTrendChartProps {
  homeTeam: string
  awayTeam: string
  allMatches: PoolMatch[]
  poolSlug: string
  /** Terza squadra da confrontare (selezione dalla classifica). */
  extraTeam?: string | null
  /**
   * Giornata della partita in visione: la linea resta **piena** fino a lì e
   * diventa **tratteggiata** sulle giornate successive (se già giocate), così si
   * distingue lo stato al momento della gara da quello che è venuto dopo.
   */
  upToGiornata?: number | null
  /**
   * Cambiandolo si ricrea l'istanza del grafico.
   *
   * Serve dopo un ridimensionamento del contenitore: il `ResizeObserver` di
   * Chart.js riceve più misure raggruppate in un solo callback e ne applica una
   * sola, quindi il canvas resta su una larghezza intermedia. Ricreare il chart
   * con il contenitore ormai stabile è deterministico.
   */
  resizeKey?: number | string
}

type TooltipState = {
  open: boolean
  /** Coordinate (viewport) del punto cliccato: da qui si posiziona la card. */
  anchorX: number
  anchorY: number
  team: { name: string; color: string } | null
  point: ProgressionPoint | null
  pinned: boolean
}

interface ProgressionPoint {
  label: string
  ratio: number
  result: string | null
  sets: string | null
  homeTeam: string
  awayTeam: string
  homeLogoSrc: string | null
  awayLogoSrc: string | null
}

const TOOLTIP_OFFSET = 14
const VIEWPORT_MARGIN = 12

/** Progressione di una squadra: un punto per giornata giocata. */
function buildProgression(teamKey: string, played: PoolMatch[]): ProgressionPoint[] {
  const prog: ProgressionPoint[] = []
  for (const match of played) {
    const totals = parseSetPoints(match.sets)
    if (!totals) continue
    const mHomeKey = normalizeTeamKey(match.home)
    const mAwayKey = normalizeTeamKey(match.away)
    if (mHomeKey !== teamKey && mAwayKey !== teamKey) continue
    const isHome = mHomeKey === teamKey
    prog.push({
      label: `G${match.giornata}`,
      ratio: isHome
        ? Number((totals.home / Math.max(1, totals.away)).toFixed(3))
        : Number((totals.away / Math.max(1, totals.home)).toFixed(3)),
      result: match.result,
      sets: match.sets,
      homeTeam: match.home,
      awayTeam: match.away,
      homeLogoSrc: match.homeLogoSrc,
      awayLogoSrc: match.awayLogoSrc,
    })
  }
  return prog
}

export function TeamTrendChart({ homeTeam, awayTeam, allMatches, poolSlug, extraTeam = null, resizeKey, upToGiornata = null }: TeamTrendChartProps) {
  const chartRef = useRef<Chart<'line'> | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const [tooltip, setTooltip] = useState<TooltipState>({ open: false, anchorX: 0, anchorY: 0, team: null, point: null, pinned: false })

  // Posiziona la card con la **sua** misura reale (su mobile la larghezza è
  // fluida e l'altezza dipende dal contenuto: flip e clamp non possono basarsi
  // su costanti). In hover resta agganciata al punto; da pinnata si sposta sul
  // lato del grafico che copre meno nodi, perché solo lì intercetta i tocchi.
  // Resta nascosta finché non è posizionata (niente lampo in alto a sinistra).
  useEffect(() => {
    if (!tooltip.open) return
    const el = tooltipRef.current
    if (!el) return
    const width = el.offsetWidth
    const height = el.offsetHeight
    const vw = window.innerWidth
    const vh = window.innerHeight
    let left = tooltip.anchorX + TOOLTIP_OFFSET
    if (left + width + VIEWPORT_MARGIN > vw) {
      const flipped = tooltip.anchorX - width - TOOLTIP_OFFSET
      // Se non c'è spazio né a destra né a sinistra (schermi stretti) la card si
      // centra sul punto: resta agganciata a quello che si è cliccato.
      left = flipped >= VIEWPORT_MARGIN ? flipped : tooltip.anchorX - width / 2
    }
    left = Math.min(Math.max(VIEWPORT_MARGIN, left), Math.max(VIEWPORT_MARGIN, vw - width - VIEWPORT_MARGIN))

    const clampTop = (value: number) =>
      Math.min(Math.max(VIEWPORT_MARGIN, value), Math.max(VIEWPORT_MARGIN, vh - height - VIEWPORT_MARGIN))
    const canvasRect = chartRef.current?.canvas.getBoundingClientRect() ?? null
    let top: number
    if (canvasRect && tooltip.pinned) {
      // Solo da pinnato la card intercetta i tocchi (`pointer-events`): qui va
      // tolta da sopra i nodi, altrimenti non si riesce più a selezionarne un
      // altro. Sceglie il lato che copre **meno** il grafico.
      const gap = 8
      const above = clampTop(canvasRect.top - height - gap)
      const below = clampTop(canvasRect.bottom + gap)
      const overlap = (value: number) =>
        Math.max(0, Math.min(value + height, canvasRect.bottom) - Math.max(value, canvasRect.top))
      top = overlap(above) <= overlap(below) ? above : below
    } else {
      // In hover (nessun blocco: `pointer-events-none`) resta agganciata al punto.
      const raw = tooltip.anchorY - height - TOOLTIP_OFFSET
      top = clampTop(raw < VIEWPORT_MARGIN ? tooltip.anchorY + TOOLTIP_OFFSET : raw)
    }
    el.style.left = `${left}px`
    el.style.top = `${top}px`
    el.style.visibility = 'visible'
  }, [tooltip])

  /**
   * Le serie del grafico. Sempre le due contendenti (tinta del girone per la
   * casa, arancio per gli ospiti); la terza — quella scelta nella classifica —
   * si aggiunge solo se è una squadra diversa dalle due in campo, in oro.
   */
  const series = useMemo(() => {
    const played = allMatches
      .filter((m) => m.played && m.sets)
      .sort((a, b) => a.giornata - b.giornata)

    const list: { name: string; color: string; progression: ProgressionPoint[] }[] = [
      { name: homeTeam, color: getPoolAccentColor(poolSlug) ?? '#2f6ac4', progression: buildProgression(normalizeTeamKey(homeTeam), played) },
      { name: awayTeam, color: '#e07840', progression: buildProgression(normalizeTeamKey(awayTeam), played) },
    ]

    const extraKey = extraTeam ? normalizeTeamKey(extraTeam) : null
    if (extraKey && extraKey !== normalizeTeamKey(homeTeam) && extraKey !== normalizeTeamKey(awayTeam)) {
      list.push({ name: extraTeam as string, color: '#f4b400', progression: buildProgression(extraKey, played) })
    }

    return list.map((s) => ({ ...s, map: new Map(s.progression.map((p) => [p.label, p] as const)) }))
  }, [homeTeam, awayTeam, extraTeam, allMatches, poolSlug])

  const labels = useMemo(
    () => [...new Set(series.flatMap((s) => s.progression.map((p) => p.label)))]
      .sort((a, b) => parseInt(a.replace('G', '')) - parseInt(b.replace('G', ''))),
    [series],
  )

  // Ultimo punto (giornata) che sta dentro il limite: da lì in poi la linea è
  // tratteggiata. `-1` = nessun limite.
  const splitIndex = useMemo(() => {
    if (upToGiornata == null) return -1
    let last = -1
    labels.forEach((label, index) => {
      const giornata = Number.parseInt(label.replace('G', ''), 10)
      if (Number.isFinite(giornata) && giornata <= upToGiornata) last = index
    })
    return last
  }, [labels, upToGiornata])

  const data: ChartData<'line'> = {
    labels,
    datasets: [
      {
        label: 'Spartiacque',
        data: labels.map(() => 1),
        borderColor: 'rgba(107, 122, 144, 0.6)',
        borderWidth: 1.5,
        borderDash: [6, 6],
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 0,
        fill: false,
        tension: 0,
      },
      ...series.map((s) => ({
        label: s.name,
        data: labels.map((l) => s.map.get(l)?.ratio ?? null),
        borderColor: s.color,
        backgroundColor: `${s.color}22`,
        borderWidth: 3,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointHitRadius: 14,
        spanGaps: true,
        tension: 0.35,
        // Tratto tratteggiato e pallini vuoti sulle giornate successive a quella
        // della partita in visione.
        segment: {
          borderDash: (ctx: ScriptableLineSegmentContext) =>
            splitIndex >= 0 && ctx.p0DataIndex >= splitIndex ? [6, 6] : undefined,
        },
        pointBackgroundColor: (ctx: ScriptableContext<'line'>) =>
          splitIndex >= 0 && ctx.dataIndex > splitIndex ? '#ffffff' : s.color,
        pointBorderColor: s.color,
        pointBorderWidth: 2,
      })),
    ],
  }

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    interaction: { mode: 'nearest', intersect: true },
    events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#6b7a90', font: { size: 11 } },
      },
      y: {
        beginAtZero: false,
        grid: { color: 'rgba(107,122,144,0.14)' },
        ticks: { color: '#6b7a90', callback: (value) => formatDecimal(Number(value), 2) },
      },
    },
  }

  const updateTooltipFromEvent = (event: MouseEvent<HTMLDivElement>, pinned: boolean) => {
    const chart = chartRef.current
    if (!chart) return

    const activeElements = chart.getElementsAtEventForMode(event.nativeEvent, 'nearest', { intersect: true }, false)
    const activePoint = activeElements[0]
    if (!activePoint || activePoint.datasetIndex <= 0) {
      if (!pinned) setTooltip((c) => (c.open && !c.pinned ? { ...c, open: false } : c))
      return
    }

    const activeSeries = series[activePoint.datasetIndex - 1]
    const label = labels[activePoint.index]
    if (!activeSeries || !label) return

    const point = activeSeries.map.get(label)
    const teamName = activeSeries.name
    const color = activeSeries.color
    if (!point) return

    const rect = chart.canvas.getBoundingClientRect()
    const metaPoint = chart.getDatasetMeta(activePoint.datasetIndex).data[activePoint.index]
    const anchorX = rect.left + (metaPoint?.x ?? 0)
    const anchorY = rect.top + (metaPoint?.y ?? 0)

    setTooltip((c) => {
      if (c.open && c.anchorX === anchorX && c.anchorY === anchorY && c.team?.name === teamName && c.point?.label === label && c.pinned === pinned) return c
      return { open: true, anchorX, anchorY, team: { name: teamName, color }, point, pinned }
    })
  }

  const hideTooltip = () => setTooltip((c) => (c.open ? { ...c, open: false, pinned: false } : c))

  if (labels.length === 0) {
    return (
      <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-[color:var(--league-line)] text-[14px] text-[color:var(--league-muted)]">
        Nessun dato disponibile per il trend.
      </div>
    )
  }

  const isTooltipTeamHome = tooltip.team && normalizeTeamKey(tooltip.team.name) === normalizeTeamKey(tooltip.point?.homeTeam ?? '')

  return (
    <>
      <div className="flex h-full flex-col">
        {/* Legenda: con tre linee non etichettate non si capisce chi è chi. */}
        <div className="mb-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          {series.map((s) => (
            <span key={s.name} className="flex min-w-0 items-center gap-1.5 text-[12px] font-medium text-[color:var(--league-muted)]">
              <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
              <span className="truncate">{shortTeamName(s.name)}</span>
            </span>
          ))}
        </div>
        <div
          className="min-h-0 flex-1"
          onMouseMove={(e) => { if (!tooltip.pinned) updateTooltipFromEvent(e, false) }}
          onClick={(e) => updateTooltipFromEvent(e, true)}
          onMouseLeave={() => { if (!tooltip.pinned) hideTooltip() }}
        >
          <Line key={resizeKey} ref={chartRef} data={data} options={options} />
        </div>
      </div>

      {tooltip.open && tooltip.team && tooltip.point ? (
        <div
          ref={tooltipRef}
          className={`fixed z-[var(--z-dropdown)] w-[236px] max-w-[calc(100vw-24px)] overflow-hidden rounded-2xl border border-[color:var(--league-line)] bg-[color:var(--league-surface)] shadow-[var(--league-shadow-md)] sm:w-[260px] ${tooltip.pinned ? 'pointer-events-auto' : 'pointer-events-none'}`}
          style={{ left: 0, top: 0, visibility: 'hidden' }}
        >
          <div className="relative px-3 py-2.5 text-white sm:px-4 sm:py-3" style={{ backgroundColor: tooltip.team.color }}>
            {tooltip.pinned ? (
              <button type="button" onClick={hideTooltip} className="league-press absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25" aria-label="Chiudi tooltip"><CloseIcon className="h-3.5 w-3.5" /></button>
            ) : null}
            <div className="pr-10">
              <div className="text-[12px] font-semibold uppercase tracking-[0.16em] text-white/80">{tooltip.point.label}</div>
              <div className="mt-1 font-display text-[17px] font-bold leading-none sm:text-[20px]">{tooltip.team.name}</div>
            </div>
            <div className="mt-2 text-[20px] font-bold leading-none tracking-[-0.04em] sm:text-[24px]">{formatDecimal(tooltip.point.ratio)}</div>
          </div>

          <div className="px-3 py-2.5 sm:px-4 sm:py-3">
            <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[color:var(--league-muted)]">Match</div>
            <div className="mt-2 rounded-xl bg-[color:var(--league-surface-alt)] px-3 py-2.5 sm:py-3">
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                <div className="min-w-0 text-center">
                  {tooltip.point.homeLogoSrc ? (
                    <img src={logoProxyUrl(tooltip.point.homeLogoSrc)} alt={tooltip.point.homeTeam} loading="lazy" decoding="async" className="mx-auto h-8 w-8 rounded-full object-contain sm:h-9 sm:w-9" />
                  ) : (
                    <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--league-line)] text-[12px] font-bold text-[color:var(--league-ink)] sm:h-9 sm:w-9">{teamInitials(tooltip.point.homeTeam)}</div>
                  )}
                  <div className="mt-2 truncate text-[12px] font-semibold text-[color:var(--league-ink)]">{shortTeamName(tooltip.point.homeTeam)}</div>
                </div>
                <div className="text-center">
                  <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[color:var(--league-muted)]">Risultato</div>
                  <div className="mt-1 text-[18px] font-bold leading-none text-[color:var(--league-ink)] sm:text-[20px]">{tooltip.point.result ?? 'n.d.'}</div>
                  <div className="mt-1 text-[12px] font-semibold uppercase tracking-[0.14em] text-[color:var(--league-muted)]">vs</div>
                </div>
                <div className="min-w-0 text-center">
                  {tooltip.point.awayLogoSrc ? (
                    <img src={logoProxyUrl(tooltip.point.awayLogoSrc)} alt={tooltip.point.awayTeam} loading="lazy" decoding="async" className="mx-auto h-8 w-8 rounded-full object-contain sm:h-9 sm:w-9" />
                  ) : (
                    <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--league-line)] text-[12px] font-bold text-[color:var(--league-ink)] sm:h-9 sm:w-9">{teamInitials(tooltip.point.awayTeam)}</div>
                  )}
                  <div className="mt-2 truncate text-[12px] font-semibold text-[color:var(--league-ink)]">{shortTeamName(tooltip.point.awayTeam)}</div>
                </div>
              </div>

              <div className="mt-3 border-t border-[color:var(--league-line)] pt-2.5">
                <div className="text-center text-[12px] font-semibold uppercase tracking-[0.14em] text-[color:var(--league-muted)]">Parziali</div>
                <div className="mt-2 flex flex-wrap justify-center gap-1.5">
                  {(tooltip.point.sets?.split(' ') ?? ['n.d.']).map((setScore, index) => {
                    const parsed = setScore.match(/^(\d{1,2})-(\d{1,2})$/)
                    const homePts = parsed ? Number(parsed[1]) : null
                    const awayPts = parsed ? Number(parsed[2]) : null
                    const teamWon = homePts != null && awayPts != null ? (isTooltipTeamHome ? homePts > awayPts : awayPts > homePts) : false
                    return (
                      <span key={`${setScore}-${index}`} className="rounded-full px-2 py-0.5 text-[11px] font-semibold shadow-sm sm:px-2.5 sm:py-1 sm:text-[12px]"
                        style={teamWon ? { backgroundColor: tooltip.team!.color, color: '#ffffff' } : { backgroundColor: '#ffffff', color: 'var(--league-ink)' }}>
                        {setScore}
                      </span>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
